// Das Menü (Fabi, Phase Kern, 17.10.2026 nachts): „Egal was gerade angezeigt wird, wenn ich Escape drücke, komme ich in
// die Übersicht.“ Es liegt über allem, hält einen laufenden Kampf an und führt zu Weiterspielen, Neuer Run, Speichern,
// Reisebericht, Kampf-Übung, Optionen, Hilfe und Startseite. Aussehen kommt später vom Grafik-Lead; das hier ist der Bau.
import { applyVolumes } from './sound.ts';
import { systemOn } from '../content/index.ts';
import { figuresWanted, speedIcon, speedLabel, toggleFigures } from './art.ts';
import { focusFirst, focusables, moveFocus } from './nav.ts';
import { setSetting, settings } from './settings.ts';

export type MenuHooks = {
  /** Gibt es einen laufenden Run, den man fortsetzen oder verlieren kann? */
  hasRun: () => boolean;
  /** Steht man gerade auf der Startseite? Dann gibt es dort schon alles Nötige */
  onTitle: () => boolean;
  newRun: () => void;
  save: () => boolean;
  chronicle: (() => void) | null;
  practice: () => void;
  /** K46: das Übungsufer */
  uebungsufer: () => void;
  toTitle: () => void;
  /** nach dem Umschalten der Figuren neu zeichnen */
  redraw: () => void;
};

type Page = 'main' | 'options' | 'help' | 'confirmNew';

const SPEEDS = [0.5, 1, 2, 4];
let hooks: MenuHooks | null = null;
let page: Page = 'main';
let note = '';

const root = (): HTMLElement => document.getElementById('menu')!;
const card = (): HTMLElement => document.getElementById('menu-card')!;

export const menuOpen = (): boolean => !root().hidden;

export function initMenu(h: MenuHooks): void {
  hooks = h;
  root().addEventListener('click', (ev) => {
    if (ev.target === root()) closeMenu();
  });
  card().addEventListener('click', (ev) => {
    const b = (ev.target as HTMLElement).closest<HTMLElement>('[data-m]');
    if (b) act(b.dataset.m!, b.dataset.v);
  });
  card().addEventListener('input', (ev) => {
    const el = ev.target as HTMLInputElement;
    const key = el.dataset.setting;
    if (key === 'master' || key === 'music' || key === 'sounds') {
      setSetting(key, Number(el.value) / 100);
      applyVolumes();
    }
  });
}

export function openMenu(): void {
  page = 'main';
  note = '';
  root().hidden = false;
  document.body.classList.add('menu-open');
  render();
}

export function closeMenu(): void {
  root().hidden = true;
  document.body.classList.remove('menu-open');
}

export function toggleMenu(): void {
  if (menuOpen()) closeMenu();
  else openMenu();
}

/** Tasten und Controller, solange das Menü offen ist */
export function menuKey(a: 'confirm' | 'start' | 'back', active: HTMLElement | null): void {
  if (a === 'back') {
    if (page === 'main') closeMenu();
    else {
      page = 'main';
      render();
    }
    return;
  }
  if (active && card().contains(active)) active.click();
  else focusFirst(card());
}

export const menuMove = (dx: number, dy: number): void => moveFocus(card(), dx, dy);

function act(m: string, v?: string): void {
  if (!hooks) return;
  switch (m) {
    case 'resume':
      return closeMenu();
    case 'new':
      if (hooks.hasRun()) {
        page = 'confirmNew';
        return render();
      }
      closeMenu();
      return hooks.newRun();
    case 'new-yes':
      closeMenu();
      return hooks.newRun();
    case 'save':
      note = hooks.save() ? 'Gespeichert. Der Spielstand bleibt in diesem Browser, auch nach dem Neuladen.' : 'Gerade gibt es keinen Run zum Speichern.';
      return render();
    case 'chronicle':
      closeMenu();
      return hooks.chronicle?.();
    case 'practice':
      closeMenu();
      return hooks.practice();
    case 'uebungsufer':
      closeMenu();
      return hooks.uebungsufer();
    case 'autopause':
      setSetting('autoPause', v === 'an');
      return render();
    case 'title':
      closeMenu();
      return hooks.toTitle();
    case 'options':
    case 'help':
    case 'main':
      page = m;
      note = '';
      return render();
    case 'speed':
      setSetting('speed', Number(v));
      return render();
    case 'figures':
      if ((v === 'an') !== figuresWanted()) toggleFigures();
      hooks.redraw();
      return render();
    case 'unfold':
      setSetting('unfold', v === 'an');
      return render();
  }
}

const btn = (m: string, label: string, opts: { v?: string; primary?: boolean; active?: boolean; small?: string; title?: string } = {}) =>
  `<button data-m="${m}"${opts.v !== undefined ? ` data-v="${opts.v}"` : ''}${opts.title ? ` title="${opts.title}" aria-label="${opts.title}"` : ''} class="${[opts.primary ? 'primary' : '', opts.active ? 'active' : ''].join(' ').trim()}">${label}${opts.small ? `<small>${opts.small}</small>` : ''}</button>`;

function render(): void {
  if (!hooks) return;
  const s = settings();
  let html = '';
  if (page === 'main') {
    html = `<h2>Menü</h2><div class="menu-list">
      ${btn('resume', 'Weiterspielen', { primary: true, small: 'Esc' })}
      ${btn('new', 'Neuer Run')}
      ${hooks.hasRun() ? btn('save', 'Speichern') : ''}
      ${hooks.chronicle ? btn('chronicle', 'Reisebericht') : ''}
      ${systemOn('uebungsufer') ? btn('uebungsufer', 'Übungsufer') : ''}
      ${btn('practice', 'Kampf-Übung')}
      ${btn('options', 'Optionen')}
      ${btn('help', 'Hilfe und Tasten')}
      ${hooks.onTitle() ? '' : btn('title', 'Zur Startseite')}
    </div>${note ? `<p class="menu-note">${note}</p>` : ''}`;
  } else if (page === 'confirmNew') {
    html = `<h2>Neuer Run?</h2><p>Der laufende Run geht dabei verloren.</p><div class="menu-list">
      ${btn('new-yes', 'Ja, neu anfangen', { primary: true })}
      ${btn('main', 'Nein, zurück')}
    </div>`;
  } else if (page === 'options') {
    const row = (title: string, buttons: string) => `<div class="menu-row"><span>${title}</span><div class="menu-choice">${buttons}</div></div>`;
    html = `<h2>Optionen</h2>
      ${row('Tempo zu Kampfbeginn', SPEEDS.map((x) => btn('speed', speedIcon(x, s.speed === x), { v: String(x), active: s.speed === x, title: speedLabel(x) })).join(''))}
      ${row('Darstellung im Kampf', btn('figures', 'Figuren', { v: 'an', active: figuresWanted() }) + btn('figures', 'Kreise', { v: 'aus', active: !figuresWanted() }))}
      ${row('Fenster entfalten sich', btn('unfold', 'an', { v: 'an', active: s.unfold }) + btn('unfold', 'aus', { v: 'aus', active: !s.unfold }))}
      ${systemOn('uebungsufer') ? row('Übungsufer: selbst anhalten, wenn ein Kniff bereit ist oder ein großer Angriff kommt', btn('autopause', 'an', { v: 'an', active: s.autoPause }) + btn('autopause', 'aus', { v: 'aus', active: !s.autoPause })) : ''}
      ${row('Lautstärke', `<input type="range" min="0" max="100" value="${Math.round(s.master * 100)}" data-setting="master" aria-label="Lautstärke gesamt">`)}
      ${row('Musik', `<input type="range" min="0" max="100" value="${Math.round(s.music * 100)}" data-setting="music" aria-label="Musik">`)}
      ${row('Geräusche', `<input type="range" min="0" max="100" value="${Math.round(s.sounds * 100)}" data-setting="sounds" aria-label="Geräusche">`)}
      <div class="menu-list">${btn('main', 'Zurück')}</div>`;
  } else {
    html = `<h2>Hilfe und Tasten</h2>
      <dl class="menu-help">
        <dt>Esc</dt><dd>dieses Menü, überall; noch einmal Esc spielt weiter</dd>
        <dt>Maus</dt><dd>klicken; beim Aufstellen Tiere aufs Ufer ziehen und auf Figuren zeigen</dd>
        <dt>Pfeile, Leertaste, Enter</dt><dd>wählen und bestätigen; Rücktaste geht einen Schritt zurück</dd>
        <dt>Im Kampf</dt><dd>Leertaste hält an, 1 bis 4 ändern das Tempo, G wechselt zwischen Figuren und Kreisen</dd>
        ${systemOn('uebungsufer') ? `<dt>Übungsufer</dt><dd>Leertaste hält an und lässt weiterlaufen. Freund wählen (Klick, Bild in der Leiste oder 1 bis 4), dann Klick auf den Boden: hingehen, auf ein Sturmwesen: angreifen. Q oder der Knopf zielt mit dem Kniff. Rechtsklick, Rücktaste oder Esc löscht den Befehl</dd>` : ''}
        <dt>Controller</dt><dd>Stick oder Steuerkreuz wählen, A bestätigen, B zurück, Start beginnt den Kampf</dd>
      </dl>
      <div class="menu-list">${btn('main', 'Zurück')}</div>`;
  }
  card().innerHTML = html;
  const keep = focusables(card()).find((e) => e.classList.contains('primary')) ?? focusables(card())[0];
  keep?.focus({ preventScroll: true });
}
